import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  CalendarDays,
  FileText,
  Info,
  LifeBuoy,
  Lightbulb,
  MousePointerClick,
  Receipt,
  ShoppingCart,
  Target,
  UserCog,
  Wallet,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  TUTORIAL_CARD_ATTRIBUTE,
} from '@/components/ui/dialog';
import { usePlanFeatures } from '@/hooks/use-plan-features';
import {
  FIRST_ACCESS_TUTORIAL_STEPS,
  setActiveOnboardingStep,
  subscribeToTutorialCommands,
  type TutorialLaunchMode,
} from './tutorial-runtime';

interface FirstAccessTutorialModalProps {
  open: boolean;
  startMode?: TutorialLaunchMode;
  userName?: string;
  onComplete: (status: 'completed' | 'skipped') => void;
}

type TutorialStep = {
  id: string;
  title: string;
  description: string;
  feature?: string;
  requiredFeatures?: string[];
  path: string;
  targetIds: string[];
  icon: typeof ArrowUpRight;
  advanceOnTargetClick?: boolean;
  manualAdvance?: boolean;
  connectorSymbols?: Array<'-' | '='>;
  interactionHint?: string;
  /** Itens destacados um por linha (ex.: os campos de um formulário). */
  bullets?: Array<{
    term: string;
    description: string;
    /** Palavras da descrição que recebem o mesmo destaque do nome do campo. */
    highlights?: string[];
  }>;
  /** Não desenha o anel de destaque — usado quando o alvo é o modal inteiro. */
  hideSpotlight?: boolean;
};

type TargetPosition = {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
};

type TourCardPosition = {
  width: number;
  left: number;
  top: number;
  placement: 'top' | 'right' | 'left' | 'bottom' | 'center';
};

type TourCardSize = {
  width: number;
  height: number;
};

type ConnectorPosition = {
  id: string;
  left: number;
  top: number;
  symbol: '-' | '=';
};

const TOUR_CARD_WIDTH = 360;
/** Balão que explica os campos de um formulário inteiro precisa de mais espaço. */
const TOUR_CARD_WIDE_WIDTH = 440;
const TOUR_CARD_HEIGHT = 210;
const TOUR_PADDING = 12;
const BACKDROP_CLASS = 'bg-black/80 backdrop-blur-[3px]';
const CLEAR_VIEW_BACKDROP_CLASS = 'bg-transparent';
const TOOLTIP_GAP = 18;
const VIEWPORT_MARGIN = 16;
const CONNECTOR_SIZE = 34;

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

/** Destaca, dentro da descrição, os termos indicados pelo passo. */
function renderDescriptionWithHighlights(description: string, highlights?: string[]) {
  if (!highlights?.length) return description;

  const pattern = new RegExp(`(${highlights.map(escapeRegExp).join('|')})`, 'g');

  return description.split(pattern).map((part, index) =>
    highlights.includes(part) ? (
      <strong key={`${part}-${index}`} className="font-bold text-emerald-700">
        {part}
      </strong>
    ) : (
      part
    )
  );
}

const CLEAR_VIEW_STEP_IDS = new Set([
  // Passos que acontecem com um modal aberto: escurecer esconderia o formulário.
  FIRST_ACCESS_TUTORIAL_STEPS.revenueSave,
  FIRST_ACCESS_TUTORIAL_STEPS.revenueReceive,
  FIRST_ACCESS_TUTORIAL_STEPS.revenueConfirm,
  FIRST_ACCESS_TUTORIAL_STEPS.futureSave,
  FIRST_ACCESS_TUTORIAL_STEPS.futureInvest,
  FIRST_ACCESS_TUTORIAL_STEPS.futureConfirm,
  FIRST_ACCESS_TUTORIAL_STEPS.monthlySave,
  FIRST_ACCESS_TUTORIAL_STEPS.monthlyPay,
  FIRST_ACCESS_TUTORIAL_STEPS.monthlyConfirm,
  FIRST_ACCESS_TUTORIAL_STEPS.dailySave,
  FIRST_ACCESS_TUTORIAL_STEPS.dailyLaunch,
  FIRST_ACCESS_TUTORIAL_STEPS.dailyConfirm,
  FIRST_ACCESS_TUTORIAL_STEPS.myAccountNav,
  FIRST_ACCESS_TUTORIAL_STEPS.agentNav,
  FIRST_ACCESS_TUTORIAL_STEPS.agentSave,
  FIRST_ACCESS_TUTORIAL_STEPS.budgetPeriodNav,
  FIRST_ACCESS_TUTORIAL_STEPS.mentoriaNav,
  FIRST_ACCESS_TUTORIAL_STEPS.supportNav,
  FIRST_ACCESS_TUTORIAL_STEPS.helpCenterNav,
]);

const TOUR_STEPS: TutorialStep[] = [
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.dashboardNav,
    title: 'Dashboard',
    description: 'No Dashboard, você encontra os principais relatórios do sistema.',
    requiredFeatures: ['dashboard-financeiro'],
    path: '/financial',
    targetIds: ['nav-dashboard'],
    icon: Wallet,
    manualAdvance: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.dashboardPeriodCard,
    title: 'Período Orçamentário',
    description:
      'É o período de 28, 29, 30 ou 31 dias, dependendo do mês, compreendido entre a receita de um mês e a receita do mês seguinte. Seu início é definido pelo dia em que você recebe sua principal receita, e o fim é o dia imediatamente anterior ao próximo recebimento.',
    requiredFeatures: ['dashboard-financeiro'],
    path: '/financial',
    targetIds: ['dashboard-period-card'],
    icon: Wallet,
    manualAdvance: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.dashboardPeriodArrows,
    title: 'Navegação do Período',
    description: 'Use as setas para avançar ou retornar no período orçamentário.',
    requiredFeatures: ['dashboard-financeiro'],
    path: '/financial',
    targetIds: ['dashboard-period-arrow-next'],
    icon: Wallet,
    manualAdvance: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.revenueCard,
    title: 'Receitas',
    description: 'Registre aqui as suas receitas.',
    feature: 'gestao-de-receitas',
    requiredFeatures: ['dashboard-financeiro'],
    path: '/financial',
    targetIds: ['summary-revenue'],
    icon: ArrowUpRight,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.revenueCreate,
    title: 'Nova Receita',
    description: 'Abra o cadastro da receita.',
    feature: 'gestao-de-receitas',
    path: '/financial/revenues',
    targetIds: ['create-revenue'],
    icon: ArrowUpRight,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.revenueSave,
    title: 'Preencha os campos',
    description:
      'Escolha, na listagem, um lançamento que você já tem. Quando terminar, o botão Criar fica verde.',
    bullets: [
      { term: 'Nome da receita', description: 'Identifique na listagem uma receita que você já tem e selecione.' },
      {
        term: 'Tipo',
        description:
          'Contínua repete todo período (salário); Única acontece uma vez; Parcelada tem valor fixo em N parcelas; Fracionada é um total recebido em várias vezes dentro do período orçamentário.',
        highlights: ['Contínua', 'Única', 'Parcelada', 'Fracionada'],
      },
      { term: 'Valor a receber', description: 'Quanto espera receber. Na parcelada, o valor de cada parcela.' },
      { term: 'Data', description: 'O dia previsto para o dinheiro entrar; posiciona a receita no período.' },
      { term: 'Fonte Pagadora', description: 'De onde vem o dinheiro: a empresa, o cliente ou quem paga.' },
      { term: 'Agente da Receita', description: 'Quem recebe — permite separar depois quanto entrou por pessoa.' },
    ],
    feature: 'gestao-de-receitas',
    path: '/financial/revenues',
    targetIds: ['revenue-create-dialog'],
    icon: ArrowUpRight,
    interactionHint: 'Lance a receita e clique em Criar Receita para continuar.',
    hideSpotlight: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.revenueReceive,
    title: 'Receber Receita',
    description: 'Clique para receber a receita.',
    feature: 'gestao-de-receitas',
    path: '/financial/revenues',
    targetIds: ['receive-revenue'],
    icon: Wallet,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.revenueConfirm,
    title: 'Confirme o recebimento',
    description:
      'Os dados já vêm preenchidos com o que foi planejado. Ajuste o que mudou e salve.',
    bullets: [
      { term: 'Planejado', description: 'Só leitura: data e valor que você havia previsto, para comparar.' },
      { term: 'Valor recebido', description: 'Quanto entrou de fato. Vem com o previsto; ajuste se recebeu diferente.' },
      { term: 'Forma de recebimento', description: 'Como o dinheiro entrou: PIX, boleto, transferência.' },
      { term: 'Data do recebimento', description: 'O dia em que o dinheiro realmente entrou.' },
      { term: 'Quem recebeu', description: 'O agente que recebeu essa receita.' },
    ],
    feature: 'gestao-de-receitas',
    path: '/financial/revenues',
    targetIds: ['revenue-realize-dialog'],
    icon: Wallet,
    hideSpotlight: true,
    interactionHint: 'Confira os dados e clique em Salvar para continuar.',
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.futureCard,
    title: 'Despesas Futuras',
    description: 'Registre aqui a sua despesa futura.',
    feature: 'despesas-futuras',
    requiredFeatures: ['dashboard-financeiro'],
    path: '/financial',
    targetIds: ['summary-future'],
    icon: Target,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.futureCreate,
    title: 'Nova Despesa Futura',
    description: 'Abra o cadastro da despesa futura.',
    feature: 'despesas-futuras',
    path: '/financial/future-expenses',
    targetIds: ['create-future-expense'],
    icon: Target,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.futureSave,
    title: 'Preencha os campos',
    description:
      'Escolha, na listagem, um objetivo que você já tem. Quando terminar, o botão Criar fica verde.',
    bullets: [
      { term: 'Nome', description: 'Identifique na listagem uma despesa futura que você já tem e selecione.' },
      { term: 'Investimento Inicial', description: 'Quanto você já tem guardado hoje. Sem nada guardado, deixe zero.' },
      { term: 'Quero', description: 'O valor total que precisa juntar para realizar o objetivo.' },
      { term: 'Para Quando', description: 'A data em que quer ter o valor completo; define em quantos meses juntar.' },
      { term: 'Taxa de Juros (a.m.)', description: 'Rendimento mensal esperado; reduz o quanto precisa guardar por mês.' },
      { term: 'Valor a Investir', description: 'Calculado pelo sistema: quanto guardar por período, já com os juros.' },
      { term: 'Categoria', description: 'Agrupa o objetivo para acompanhar em que áreas você investe.' },
      { term: 'Agente', description: 'Quem é responsável por esse objetivo.' },
    ],
    feature: 'despesas-futuras',
    path: '/financial/future-expenses',
    targetIds: ['future-create-dialog'],
    icon: Target,
    interactionHint: 'Lance a despesa e clique em Criar Meta para continuar.',
    hideSpotlight: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.futureInvest,
    title: 'Investir',
    description: 'Clique para registrar o investimento.',
    feature: 'despesas-futuras',
    path: '/financial/future-expenses',
    targetIds: ['invest-future-expense'],
    icon: Wallet,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.futureConfirm,
    title: 'Confirme o investimento',
    description:
      'Os dados já vêm preenchidos com o que foi planejado. Ajuste o que mudou e salve.',
    bullets: [
      { term: 'Data do Investimento', description: 'O dia em que você aplicou o dinheiro.' },
      { term: 'Local do Investimento', description: 'Onde o dinheiro foi aplicado: poupança, CDB, fundo.' },
      { term: 'Valor do Investimento', description: 'Quanto você guardou de fato neste período.' },
      { term: 'Quem Investiu', description: 'O agente responsável pelo aporte.' },
      { term: 'Observações', description: 'Opcional: qualquer detalhe que queira registrar.' },
    ],
    feature: 'despesas-futuras',
    path: '/financial/future-expenses',
    targetIds: ['future-realize-dialog'],
    icon: Wallet,
    hideSpotlight: true,
    interactionHint: 'Confira os dados e clique em Salvar para continuar.',
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.monthlyCard,
    title: 'Despesas Mensais',
    description: 'Registre aqui a sua despesa mensal.',
    feature: 'despesas-mensais',
    requiredFeatures: ['dashboard-financeiro'],
    path: '/financial',
    targetIds: ['summary-monthly'],
    icon: Receipt,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.monthlyCreate,
    title: 'Nova Despesa',
    description: 'Abra o cadastro da despesa mensal.',
    feature: 'despesas-mensais',
    path: '/financial/monthly-expenses',
    targetIds: ['create-monthly-expense'],
    icon: Receipt,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.monthlySave,
    title: 'Preencha os campos',
    description:
      'Escolha, na listagem, uma conta que você já tem. Quando terminar, o botão Criar fica verde.',
    bullets: [
      { term: 'Nome', description: 'Identifique na listagem uma despesa mensal que você já tem e selecione.' },
      {
        term: 'Tipo',
        description:
          'Contínua repete todo período (aluguel); Única acontece uma vez; Parcelada tem valor fixo em N parcelas.',
        highlights: ['Contínua', 'Única', 'Parcelada'],
      },
      { term: 'Valor', description: 'Quanto paga por período. Na parcelada, o valor de cada parcela.' },
      { term: 'Vencimento', description: 'O dia em que a conta vence; organiza a despesa no período.' },
      { term: 'Categoria', description: 'Agrupa a despesa para enxergar em que áreas o dinheiro sai.' },
      { term: 'Agente', description: 'Quem é responsável por pagar essa despesa.' },
    ],
    feature: 'despesas-mensais',
    path: '/financial/monthly-expenses',
    targetIds: ['monthly-create-dialog'],
    icon: Receipt,
    interactionHint: 'Lance a despesa e clique em Criar Despesa para continuar.',
    hideSpotlight: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.monthlyPay,
    title: 'Pagar',
    description: 'Clique para pagar a despesa.',
    feature: 'despesas-mensais',
    path: '/financial/monthly-expenses',
    targetIds: ['pay-monthly-expense'],
    icon: Wallet,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.monthlyConfirm,
    title: 'Confirme o pagamento',
    description:
      'Os dados já vêm preenchidos com o que foi planejado. Ajuste o que mudou e salve.',
    bullets: [
      { term: 'Valor pago', description: 'Quanto saiu de fato. Vem com o previsto; ajuste se pagou diferente.' },
      { term: 'Data de pagamento', description: 'O dia em que a conta foi realmente paga.' },
      { term: 'Quem pagou', description: 'O agente responsável pelo pagamento.' },
      { term: 'Forma de pagamento', description: 'Como você pagou: PIX, boleto, cartão, débito.' },
      { term: 'Observações', description: 'Opcional: qualquer detalhe que queira registrar.' },
    ],
    feature: 'despesas-mensais',
    path: '/financial/monthly-expenses',
    targetIds: ['monthly-realize-dialog'],
    icon: Wallet,
    hideSpotlight: true,
    interactionHint: 'Confira os dados e clique em Salvar para continuar.',
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.dailyCard,
    title: 'Despesas do Dia a Dia',
    description: 'Registre aqui a sua despesa do dia a dia.',
    feature: 'despesas-do-dia-a-dia',
    requiredFeatures: ['dashboard-financeiro'],
    path: '/financial',
    targetIds: ['summary-daily'],
    icon: ShoppingCart,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.dailyCreate,
    title: 'Nova Despesa',
    description: 'Abra o cadastro da despesa do dia a dia.',
    feature: 'despesas-do-dia-a-dia',
    path: '/financial/daily-expenses',
    targetIds: ['create-daily-expense'],
    icon: ShoppingCart,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.dailySave,
    title: 'Preencha os campos',
    description:
      'Escolha, na listagem, um gasto que você já tem. Quando terminar, o botão Criar fica verde.',
    bullets: [
      { term: 'Nome', description: 'Identifique na listagem um gasto do dia a dia que você já tem e selecione.' },
      { term: 'Frequência da despesa', description: 'Quantas vezes no período costuma ter esse gasto. Ex.: almoço fora 4 vezes.' },
      { term: 'Valor médio planejado', description: 'Quanto costuma custar cada vez. É uma média, não precisa ser exato.' },
      { term: 'Valor total a gastar', description: 'Calculado pelo sistema: frequência x valor médio.' },
      { term: 'Categoria', description: 'Agrupa o gasto para enxergar em que áreas o dinheiro sai.' },
      { term: 'Agente da despesa', description: 'Quem costuma fazer esse gasto.' },
    ],
    feature: 'despesas-do-dia-a-dia',
    path: '/financial/daily-expenses',
    targetIds: ['daily-create-dialog'],
    icon: ShoppingCart,
    interactionHint: 'Lance a despesa e clique em Criar Despesa para continuar.',
    hideSpotlight: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.dailyLaunch,
    title: 'Lançar',
    description: 'Clique para lançar a despesa.',
    feature: 'despesas-do-dia-a-dia',
    path: '/financial/daily-expenses',
    targetIds: ['launch-daily-expense'],
    icon: Wallet,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.dailyConfirm,
    title: 'Confirme o lançamento',
    description:
      'Os dados já vêm preenchidos com o que foi planejado. Ajuste o que mudou e salve.',
    bullets: [
      { term: 'Valor pago', description: 'Quanto você gastou de fato nesta vez.' },
      { term: 'Data de pagamento', description: 'O dia em que o gasto aconteceu.' },
      { term: 'Quem pagou', description: 'O agente que fez esse gasto.' },
      { term: 'Forma de pagamento', description: 'Como você pagou: PIX, dinheiro, cartão, débito.' },
      { term: 'Observações', description: 'Opcional: qualquer detalhe que queira registrar.' },
    ],
    feature: 'despesas-do-dia-a-dia',
    path: '/financial/daily-expenses',
    targetIds: ['daily-realize-dialog'],
    icon: Wallet,
    hideSpotlight: true,
    interactionHint: 'Confira os dados e clique em Salvar para continuar.',
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.balanceRevenue,
    title: 'Entenda seu Saldo',
    description: 'As receitas são a base do seu saldo.',
    requiredFeatures: ['dashboard-financeiro', 'gestao-de-receitas'],
    path: '/financial',
    targetIds: ['summary-revenue'],
    icon: Wallet,
    manualAdvance: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.balanceFuture,
    title: 'Entenda seu Saldo',
    description: 'Receitas menos Despesas Futuras.',
    requiredFeatures: ['dashboard-financeiro', 'gestao-de-receitas', 'despesas-futuras'],
    path: '/financial',
    targetIds: ['summary-revenue', 'summary-future'],
    icon: Wallet,
    manualAdvance: true,
    connectorSymbols: ['-'],
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.balanceMonthly,
    title: 'Entenda seu Saldo',
    description: 'Subtraia também as Despesas Mensais.',
    requiredFeatures: ['dashboard-financeiro', 'gestao-de-receitas', 'despesas-futuras', 'despesas-mensais'],
    path: '/financial',
    targetIds: ['summary-revenue', 'summary-future', 'summary-monthly'],
    icon: Wallet,
    manualAdvance: true,
    connectorSymbols: ['-', '-'],
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.balanceDaily,
    title: 'Entenda seu Saldo',
    description: 'Depois, subtraia as Despesas do Dia a Dia.',
    requiredFeatures: ['dashboard-financeiro', 
      'gestao-de-receitas',
      'despesas-futuras',
      'despesas-mensais',
      'despesas-do-dia-a-dia',
    ],
    path: '/financial',
    targetIds: ['summary-revenue', 'summary-future', 'summary-monthly', 'summary-daily'],
    icon: Wallet,
    manualAdvance: true,
    connectorSymbols: ['-', '-', '-'],
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.balanceResult,
    title: 'Entenda seu Saldo',
    description: 'O resultado dessa conta é o seu saldo.',
    requiredFeatures: [
      'gestao-de-receitas',
      'despesas-futuras',
      'despesas-mensais',
      'despesas-do-dia-a-dia',
    ],
    path: '/financial',
    targetIds: [
      'summary-revenue',
      'summary-future',
      'summary-monthly',
      'summary-daily',
      'summary-balance',
    ],
    icon: Wallet,
    manualAdvance: true,
    connectorSymbols: ['-', '-', '-', '='],
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.balanceVariation,
    title: 'Entenda a Variação',
    description: 'Variação é a diferença entre o planejado e o realizado.',
    requiredFeatures: ['dashboard-financeiro', 'gestao-de-receitas'],
    path: '/financial',
    targetIds: ['summary-revenue-variation'],
    icon: Wallet,
    manualAdvance: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.reportsNav,
    title: 'Relatórios',
    description:
      'Nesta página Relatórios você encontrará os principais relatórios que vão te ajudar a analisar a sua saúde financeira.',
    requiredFeatures: ['relatorios-financeiros'],
    path: '/financial',
    targetIds: ['nav-reports'],
    icon: FileText,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.myAccountNav,
    title: 'Minha Conta',
    description:
      'Na página Minha Conta é onde você poderá editar os seus dados, trocar senha, pagar faturas e alterar o seu plano.',
    requiredFeatures: ['minha-conta'],
    path: '/financial/reports',
    targetIds: ['nav-my-account'],
    icon: UserCog,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.agentNav,
    title: 'Agentes',
    description: 'Abra aqui o cadastro dos seus agentes.',
    requiredFeatures: ['agentes'],
    path: '/account',
    targetIds: ['nav-agents'],
    icon: Wallet,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.agentCreate,
    title: 'Novo Agente',
    description:
      'Agentes são as pessoas, bens e animais de estimação que geram receitas ou despesas no seu orçamento.',
    requiredFeatures: ['agentes'],
    path: '/financial/agents',
    targetIds: ['create-agent'],
    icon: Wallet,
    advanceOnTargetClick: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.agentSave,
    title: 'Preencha os campos',
    description:
      'Cadastre um agente de verdade: uma pessoa, um bem ou um pet que gera receita ou despesa no seu orçamento.',
    bullets: [
      { term: '1 - Nome', description: 'Use o primeiro nome ou apelido. Ex.: Maria, Carro, Totó.' },
      {
        term: '2 - Tipo',
        description:
          'Pessoa é gente da casa ou da empresa; Bem é algo que gera custo, como carro ou imóvel; Pet é animal de estimação.',
        highlights: ['Pessoa', 'Bem', 'Pet'],
      },
      { term: 'Tem receita?', description: 'Marque se esse agente traz dinheiro para o orçamento.' },
      { term: 'Paga contas?', description: 'Marque se ele é responsável por pagar despesas.' },
      {
        term: 'Agente Padrão?',
        description: 'Marque para ele vir pré-selecionado nos próximos lançamentos.',
      },
      { term: 'Descrição', description: 'Opcional: uma observação para você lembrar quem é.' },
    ],
    requiredFeatures: ['agentes'],
    path: '/financial/agents',
    targetIds: ['agent-create-dialog'],
    icon: UserCog,
    hideSpotlight: true,
    interactionHint: 'Cadastre o agente e clique em Criar Agente para continuar.',
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.budgetPeriodNav,
    title: 'Período Orçamentário',
    description:
      'Nesta página você poderá alterar o seu Período Orçamentário, caso tenha alteração nos seus principais recebimentos.',
    requiredFeatures: ['minha-conta'],
    path: '/account/budget-period',
    targetIds: ['nav-budget-period'],
    icon: CalendarDays,
    manualAdvance: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.mentoriaNav,
    title: 'Mentoria Librattum',
    description:
      'Aqui você conhece a Mentoria Librattum e agenda uma conversa on-line com nossos consultores para tirar suas dúvidas.',
    requiredFeatures: ['mentoria-librattum'],
    path: '/account/mentoria',
    targetIds: ['nav-mentoria'],
    icon: Lightbulb,
    manualAdvance: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.supportNav,
    title: 'Suporte',
    description:
      'Nesta página você pode abrir um ticket para nosso time de suporte, caso necessite de auxílio.',
    requiredFeatures: ['suporte'],
    path: '/support',
    targetIds: ['nav-support'],
    icon: LifeBuoy,
    manualAdvance: true,
  },
  {
    id: FIRST_ACCESS_TUTORIAL_STEPS.helpCenterNav,
    title: 'Central de ajuda',
    description: 'Nesta página você vai encontrar tutorias e orientações da plataforma.',
    path: '/support/help',
    targetIds: ['nav-help-center'],
    icon: Info,
    manualAdvance: true,
  },
];

function isElementVisible(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();

  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.opacity !== '0' &&
    rect.width > 0 &&
    rect.height > 0
  );
}

function getTargetElements(targetIds: string[]) {
  const seen = new Set<HTMLElement>();
  const elements: HTMLElement[] = [];

  for (const targetId of targetIds) {
    const candidates = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour-id="${targetId}"]`));
    const visibleElement = candidates.find((element) => isElementVisible(element));

    if (visibleElement && !seen.has(visibleElement)) {
      seen.add(visibleElement);
      elements.push(visibleElement);
    }
  }

  return elements;
}

function isScrollableContainer(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY;
  const overflowX = style.overflowX;
  const canScrollVertically =
    ['auto', 'scroll', 'overlay'].includes(overflowY) &&
    element.scrollHeight > element.clientHeight + 1;
  const canScrollHorizontally =
    ['auto', 'scroll', 'overlay'].includes(overflowX) &&
    element.scrollWidth > element.clientWidth + 1;

  return canScrollVertically || canScrollHorizontally;
}

function getScrollableAncestors(element: HTMLElement) {
  const ancestors: HTMLElement[] = [];
  let currentParent = element.parentElement;

  while (currentParent && currentParent !== document.body) {
    if (isScrollableContainer(currentParent)) {
      ancestors.push(currentParent);
    }

    currentParent = currentParent.parentElement;
  }

  return ancestors;
}

function ensureElementVisible(element: HTMLElement, isMobile: boolean) {
  let didScroll = false;

  for (const ancestor of getScrollableAncestors(element)) {
    const ancestorRect = ancestor.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const targetScrollTop = clamp(
      ancestor.scrollTop +
        (elementRect.top - ancestorRect.top) -
        ancestor.clientHeight / 2 +
        elementRect.height / 2,
      0,
      Math.max(0, ancestor.scrollHeight - ancestor.clientHeight)
    );
    const targetScrollLeft = clamp(
      ancestor.scrollLeft +
        (elementRect.left - ancestorRect.left) -
        ancestor.clientWidth / 2 +
        elementRect.width / 2,
      0,
      Math.max(0, ancestor.scrollWidth - ancestor.clientWidth)
    );

    // Alvo maior que o container nunca fica "dentro": rolar a cada sincronização
    // só produziria tremor na tela.
    const fitsVertically = elementRect.height <= ancestorRect.height - VIEWPORT_MARGIN * 2;
    const fitsHorizontally = elementRect.width <= ancestorRect.width - VIEWPORT_MARGIN * 2;

    const needsVerticalScroll =
      fitsVertically &&
      (elementRect.top < ancestorRect.top + VIEWPORT_MARGIN ||
        elementRect.bottom > ancestorRect.bottom - VIEWPORT_MARGIN);
    const needsHorizontalScroll =
      fitsHorizontally &&
      (elementRect.left < ancestorRect.left + VIEWPORT_MARGIN ||
        elementRect.right > ancestorRect.right - VIEWPORT_MARGIN);

    if (needsVerticalScroll && Math.abs(targetScrollTop - ancestor.scrollTop) > 1) {
      ancestor.scrollTo({ top: targetScrollTop, behavior: 'auto' });
      didScroll = true;
    }

    if (needsHorizontalScroll && Math.abs(targetScrollLeft - ancestor.scrollLeft) > 1) {
      ancestor.scrollTo({ left: targetScrollLeft, behavior: 'auto' });
      didScroll = true;
    }
  }

  const rect = element.getBoundingClientRect();
  // Um alvo que não cabe na tela (um modal inteiro, por exemplo) jamais fica
  // "dentro" da viewport: centralizá-lo a cada ciclo faria a tela tremer.
  const fitsInViewport =
    rect.height <= window.innerHeight - VIEWPORT_MARGIN * 2 &&
    rect.width <= window.innerWidth - VIEWPORT_MARGIN * 2;
  const isOutsideViewport =
    rect.top < VIEWPORT_MARGIN ||
    rect.bottom > window.innerHeight - VIEWPORT_MARGIN ||
    rect.left < VIEWPORT_MARGIN ||
    rect.right > window.innerWidth - VIEWPORT_MARGIN;

  if (fitsInViewport && isOutsideViewport) {
    element.scrollIntoView({
      behavior: 'auto',
      block: 'center',
      inline: 'nearest',
    });
    didScroll = true;
  }

  return didScroll;
}

/** Diferenças de menos de 1px são ruído de layout, não movimento real. */
const POSITION_EPSILON = 1;

function isSamePosition(a: TargetPosition | null, b: TargetPosition | null) {
  if (a === b) return true;
  if (!a || !b) return false;

  return (
    Math.abs(a.top - b.top) < POSITION_EPSILON &&
    Math.abs(a.left - b.left) < POSITION_EPSILON &&
    Math.abs(a.width - b.width) < POSITION_EPSILON &&
    Math.abs(a.height - b.height) < POSITION_EPSILON
  );
}

function isSamePositionList(a: TargetPosition[], b: TargetPosition[]) {
  return a.length === b.length && a.every((position, i) => isSamePosition(position, b[i]));
}

function getTargetPositionFromElement(element: HTMLElement): TargetPosition {
  const rect = element.getBoundingClientRect();

  return {
    top: rect.top - TOUR_PADDING,
    left: rect.left - TOUR_PADDING,
    width: rect.width + TOUR_PADDING * 2,
    height: rect.height + TOUR_PADDING * 2,
    right: rect.right + TOUR_PADDING,
    bottom: rect.bottom + TOUR_PADDING,
  };
}

function getCombinedTargetPosition(targets: TargetPosition[]) {
  if (targets.length === 0) {
    return null;
  }

  const top = Math.min(...targets.map((target) => target.top));
  const left = Math.min(...targets.map((target) => target.left));
  const right = Math.max(...targets.map((target) => target.right));
  const bottom = Math.max(...targets.map((target) => target.bottom));

  return {
    top,
    left,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

function getConnectorPositions(
  targets: TargetPosition[],
  connectorSymbols: Array<'-' | '='> | undefined,
  isMobile: boolean
): ConnectorPosition[] {
  if (isMobile || !connectorSymbols?.length || targets.length < 2) {
    return [];
  }

  return connectorSymbols
    .map((symbol, index) => {
      const currentTarget = targets[index];
      const nextTarget = targets[index + 1];

      if (!currentTarget || !nextTarget) {
        return null;
      }

      const currentCenterY = currentTarget.top + currentTarget.height / 2;
      const nextCenterY = nextTarget.top + nextTarget.height / 2;
      const sameRow = Math.abs(currentCenterY - nextCenterY) <= Math.max(currentTarget.height, nextTarget.height) * 0.45;

      if (!sameRow) {
        return null;
      }

      const gapWidth = nextTarget.left - currentTarget.right;
      const left = currentTarget.right + gapWidth / 2 - CONNECTOR_SIZE / 2;
      const top = (currentCenterY + nextCenterY) / 2 - CONNECTOR_SIZE / 2;

      return {
        id: `${currentTarget.left}-${nextTarget.left}-${symbol}-${index}`,
        left,
        top,
        symbol,
      };
    })
    .filter((connector): connector is ConnectorPosition => connector !== null);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getCardPosition(
  target: TargetPosition | null,
  cardSize: TourCardSize,
  isMobile: boolean
): TourCardPosition {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cardWidth = Math.min(cardSize.width || TOUR_CARD_WIDTH, viewportWidth - VIEWPORT_MARGIN * 2);
  const cardHeight = Math.min(cardSize.height || TOUR_CARD_HEIGHT, viewportHeight - VIEWPORT_MARGIN * 2);

  if (!target || isMobile) {
    return {
      width: cardWidth,
      left: Math.max(VIEWPORT_MARGIN, (viewportWidth - cardWidth) / 2),
      top: Math.max(VIEWPORT_MARGIN, viewportHeight - cardHeight - 24),
      placement: 'center',
    };
  }

  const centerLeft = clamp(
    target.left + target.width / 2 - cardWidth / 2,
    VIEWPORT_MARGIN,
    viewportWidth - cardWidth - VIEWPORT_MARGIN
  );
  const centerTop = clamp(
    target.top + target.height / 2 - cardHeight / 2,
    VIEWPORT_MARGIN,
    viewportHeight - cardHeight - VIEWPORT_MARGIN
  );

  const candidates: Array<Omit<TourCardPosition, 'width'> & { priority: number }> = [
    {
      placement: 'top',
      left: centerLeft,
      top: target.top - cardHeight - TOOLTIP_GAP,
      priority: 0,
    },
    {
      placement: 'right',
      left: target.right + TOOLTIP_GAP,
      top: centerTop,
      priority: 1,
    },
    {
      placement: 'left',
      left: target.left - cardWidth - TOOLTIP_GAP,
      top: centerTop,
      priority: 2,
    },
    {
      placement: 'bottom',
      left: centerLeft,
      top: target.bottom + TOOLTIP_GAP,
      priority: 3,
    },
  ];

  const getOverflow = (left: number, top: number) => {
    const right = left + cardWidth;
    const bottom = top + cardHeight;

    return (
      Math.max(0, VIEWPORT_MARGIN - left) +
      Math.max(0, VIEWPORT_MARGIN - top) +
      Math.max(0, right - (viewportWidth - VIEWPORT_MARGIN)) +
      Math.max(0, bottom - (viewportHeight - VIEWPORT_MARGIN))
    );
  };

  const getOverlapArea = (left: number, top: number) => {
    const right = left + cardWidth;
    const bottom = top + cardHeight;
    const overlapWidth = Math.max(0, Math.min(right, target.right) - Math.max(left, target.left));
    const overlapHeight = Math.max(0, Math.min(bottom, target.bottom) - Math.max(top, target.top));

    return overlapWidth * overlapHeight;
  };

  const bestCandidate = candidates
    .map((candidate) => {
      const clampedLeft = clamp(
        candidate.left,
        VIEWPORT_MARGIN,
        viewportWidth - cardWidth - VIEWPORT_MARGIN
      );
      const clampedTop = clamp(
        candidate.top,
        VIEWPORT_MARGIN,
        viewportHeight - cardHeight - VIEWPORT_MARGIN
      );
      const overflow = getOverflow(candidate.left, candidate.top);
      const overlapArea = getOverlapArea(clampedLeft, clampedTop);
      const centerDistance =
        Math.abs(clampedLeft + cardWidth / 2 - (target.left + target.width / 2)) +
        Math.abs(clampedTop + cardHeight / 2 - (target.top + target.height / 2));
      const score = overflow * 10000 + overlapArea * 100 + candidate.priority * 10 + centerDistance;

      return {
        width: cardWidth,
        left: clampedLeft,
        top: clampedTop,
        placement: candidate.placement,
        score,
      };
    })
    .sort((first, second) => first.score - second.score)[0];

  if (bestCandidate) {
    return bestCandidate;
  }

  return {
    width: cardWidth,
    left: centerLeft,
    top: centerTop,
    placement: 'center',
  };
}

export function FirstAccessTutorialModal({
  open,
  startMode = 'welcome',
  userName,
  onComplete,
}: Readonly<FirstAccessTutorialModalProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasFeature } = usePlanFeatures();
  const tourCardRef = useRef<HTMLDivElement | null>(null);
  const [phase, setPhase] = useState<'welcome' | 'tour'>('welcome');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetPositions, setTargetPositions] = useState<TargetPosition[]>([]);
  const [targetPosition, setTargetPosition] = useState<TargetPosition | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [cardSize, setCardSize] = useState<TourCardSize>({
    width: TOUR_CARD_WIDTH,
    height: TOUR_CARD_HEIGHT,
  });

  const normalizedUserName = userName?.trim() ?? '';

  const availableSteps = useMemo(
    () =>
      TOUR_STEPS.filter((step) => {
        const requiredFeatures = step.requiredFeatures ?? (step.feature ? [step.feature] : []);
        return requiredFeatures.every((feature) => hasFeature(feature));
      }),
    [hasFeature]
  );

  const currentStep = availableSteps[currentStepIndex] ?? null;
  const CurrentIcon = currentStep?.icon ?? ArrowUpRight;

  const finishTutorial = useCallback(
    (status: 'completed' | 'skipped') => {
      setActiveOnboardingStep(null);
      setPhase('welcome');
      setCurrentStepIndex(0);
      setTargetPositions([]);
      setTargetPosition(null);
      onComplete(status);
    },
    [onComplete]
  );

  const advanceStep = useCallback(
    (expectedStepId?: string) => {
      if (expectedStepId && currentStep?.id !== expectedStepId) {
        return;
      }

      if (currentStepIndex >= availableSteps.length - 1) {
        finishTutorial('completed');
        return;
      }

      setCurrentStepIndex((previousIndex) => previousIndex + 1);
    },
    [availableSteps.length, currentStep?.id, currentStepIndex, finishTutorial]
  );

  const goBackStep = useCallback(() => {
    setCurrentStepIndex((previousIndex) => {
      if (previousIndex === 0) {
        setPhase('welcome');
        return 0;
      }

      return previousIndex - 1;
    });
  }, []);

  useEffect(() => {
    if (!open) return;

    setCurrentStepIndex(0);
    setTargetPositions([]);
    setTargetPosition(null);
    setPhase(startMode);
  }, [open, startMode]);

  useEffect(() => {
    if (!open || phase !== 'tour' || !currentStep) {
      setActiveOnboardingStep(null);
      return;
    }

    setActiveOnboardingStep(currentStep.id);

    return () => {
      setActiveOnboardingStep(null);
    };
  }, [open, phase, currentStep]);

  useEffect(() => {
    if (!open || phase !== 'tour' || !currentStep) return;
    if (location.pathname === currentStep.path) return;

    navigate(currentStep.path, { replace: false });
  }, [open, phase, currentStep, location.pathname, navigate]);

  useEffect(() => {
    if (!open || phase !== 'tour') return;

    return subscribeToTutorialCommands((command) => {
      if (command.type !== 'advance') return;
      advanceStep(command.stepId);
    });
  }, [open, phase, advanceStep]);

  useEffect(() => {
    if (!open || phase !== 'tour') return;

    const syncTarget = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileViewport(mobile);

      if (!currentStep) {
        setTargetPositions([]);
        setTargetPosition(null);
        return;
      }

      const elements = getTargetElements(currentStep.targetIds);
      if (elements.length === 0) {
        setTargetPositions([]);
        setTargetPosition(null);
        return;
      }

      elements.forEach((element) => {
        ensureElementVisible(element, mobile);
      });

      const syncedElements = getTargetElements(currentStep.targetIds);
      const positions = syncedElements.map(getTargetPositionFromElement);
      const combinedTarget = getCombinedTargetPosition(positions);

      if (!combinedTarget) {
        setTargetPositions([]);
        setTargetPosition(null);
        return;
      }

      // Sem mudança real de posição não há re-render: é isso que evita o card
      // "perseguir" o alvo enquanto o modal ainda está animando.
      setTargetPositions((prev) => (isSamePositionList(prev, positions) ? prev : positions));
      setTargetPosition((prev) => (isSamePosition(prev, combinedTarget) ? prev : combinedTarget));
    };

    syncTarget();

    const intervalId = window.setInterval(syncTarget, 250);
    window.addEventListener('resize', syncTarget);
    window.addEventListener('scroll', syncTarget, true);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('resize', syncTarget);
      window.removeEventListener('scroll', syncTarget, true);
    };
  }, [open, phase, currentStep, location.pathname]);

  useEffect(() => {
    if (!open || phase !== 'tour' || !currentStep?.advanceOnTargetClick) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const elements = getTargetElements(currentStep.targetIds);
      if (elements.length === 0) return;

      const clickedNode = event.target;
      if (!(clickedNode instanceof Node)) return;

      if (elements.some((element) => element.contains(clickedNode))) {
        window.setTimeout(() => {
          advanceStep(currentStep.id);
        }, 50);
      }
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [open, phase, currentStep, advanceStep]);

  useEffect(() => {
    if (!open || phase !== 'tour') return;

    const cardElement = tourCardRef.current;
    if (!cardElement) return;

    const syncCardSize = () => {
      setCardSize({
        width: cardElement.offsetWidth || TOUR_CARD_WIDTH,
        height: cardElement.offsetHeight || TOUR_CARD_HEIGHT,
      });
    };

    syncCardSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncCardSize);

      return () => {
        window.removeEventListener('resize', syncCardSize);
      };
    }

    const resizeObserver = new ResizeObserver(syncCardSize);
    resizeObserver.observe(cardElement);
    window.addEventListener('resize', syncCardSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncCardSize);
    };
  }, [open, phase, currentStep]);

  const handleStartTutorial = () => {
    if (availableSteps.length === 0) {
      finishTutorial('completed');
      return;
    }

    setPhase('tour');
    setCurrentStepIndex(0);
    navigate('/financial', { replace: false });
  };

  if (!open) {
    return null;
  }

  // O balão que explica os campos de um formulário inteiro é mais largo.
  const preferredCardWidth = currentStep?.bullets ? TOUR_CARD_WIDE_WIDTH : TOUR_CARD_WIDTH;
  const cardPosition = getCardPosition(
    targetPosition,
    { ...cardSize, width: preferredCardWidth },
    isMobileViewport
  );
  const connectorPositions = getConnectorPositions(
    targetPositions,
    currentStep?.connectorSymbols,
    isMobileViewport
  );
  const backdropClass =
    currentStep && CLEAR_VIEW_STEP_IDS.has(currentStep.id)
      ? CLEAR_VIEW_BACKDROP_CLASS
      : BACKDROP_CLASS;
  // Sem alvo na tela não há destaque para clicar: o passo passa a oferecer
  // "Seguir", senão o usuário ficaria preso, sem saída a não ser pular o tour.
  const hasVisibleTarget = Boolean(targetPosition);
  const showInteractionHint = !currentStep?.manualAdvance && hasVisibleTarget;
  const showAdvanceButton = Boolean(currentStep?.manualAdvance) || !hasVisibleTarget;

  return (
    <>
      <Dialog open={phase === 'welcome'} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader className="space-y-4 text-center">
            <DialogTitle className="text-2xl font-semibold leading-tight text-center">
              Seja bem-vindo{normalizedUserName ? `, ${normalizedUserName}` : ''}.
            </DialogTitle>
            <DialogDescription className="space-y-3 text-base leading-relaxed text-foreground sm:px-4">
              <p>É muito bom ter você utilizando a nossa metodologia.</p>
              <p>
                Para começar, preparamos um guia rápido para te mostrar como registrar suas
                primeiras movimentações e deixar seu saldo sempre em dia.
              </p>
              <p className="font-medium text-foreground">Vamos lá?</p>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-col gap-3 sm:flex-row sm:justify-center pt-4">
            <Button onClick={handleStartTutorial} className="w-full sm:w-auto">
              Quero ver o passo a passo
            </Button>
            <Button variant="outline" onClick={() => finishTutorial('skipped')} className="w-full sm:w-auto">
              Pular esta etapa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {phase === 'tour' && currentStep && (
        <>
          {targetPosition && (
            <>
              <div className={`fixed inset-x-0 top-0 z-[70] ${backdropClass}`} style={{ height: Math.max(0, targetPosition.top) }} />
              <div
                className={`fixed left-0 z-[70] ${backdropClass}`}
                style={{
                  top: Math.max(0, targetPosition.top),
                  width: Math.max(0, targetPosition.left),
                  height: Math.max(0, targetPosition.height),
                }}
              />
              <div
                className={`fixed right-0 z-[70] ${backdropClass}`}
                style={{
                  top: Math.max(0, targetPosition.top),
                  width: Math.max(0, window.innerWidth - targetPosition.right),
                  height: Math.max(0, targetPosition.height),
                }}
              />
              <div className={`fixed inset-x-0 bottom-0 z-[70] ${backdropClass}`} style={{ top: Math.max(0, targetPosition.bottom) }} />
              {!currentStep.hideSpotlight && targetPositions.map((position, index) => (
                <div
                  key={`${position.left}-${position.top}-${index}`}
                  className="pointer-events-none fixed z-[71]"
                  style={{
                    top: position.top,
                    left: position.left,
                    width: position.width,
                    height: position.height,
                  }}
                >
                  <div className="absolute inset-0 rounded-2xl border-[3px] border-emerald-400 shadow-[0_0_0_2px_rgba(16,185,129,0.55),0_0_48px_rgba(16,185,129,0.35)]" />
                </div>
              ))}

              {connectorPositions.map((connector) => (
                <div
                  key={connector.id}
                  className="pointer-events-none fixed z-[71] flex items-center justify-center rounded-full border-2 border-amber-300 bg-white text-lg font-semibold text-amber-500 shadow-lg"
                  style={{
                    top: connector.top,
                    left: connector.left,
                    width: CONNECTOR_SIZE,
                    height: CONNECTOR_SIZE,
                  }}
                >
                  {connector.symbol}
                </div>
              ))}
            </>
          )}

          {/*
            Sem alvo não há o que destacar: escurecer a tela inteira apenas
            deixaria a plataforma preta e inutilizável (era o que acontecia em
            planos sem o dashboard, cujo passo mirava um card inexistente).
          */}

          <div
            ref={tourCardRef}
            // pointer-events-auto: com um modal aberto o Radix zera os eventos
            // fora dele; sem isto os botões do card ficariam inertes.
            {...{ [TUTORIAL_CARD_ATTRIBUTE]: 'true' }}
            className="pointer-events-auto fixed z-[72] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl transition-[top,left] duration-150 ease-out motion-reduce:transition-none"
            style={{
              width: cardPosition.width,
              left: cardPosition.left,
              top: cardPosition.top,
              maxWidth: 'calc(100vw - 32px)',
            }}
          >
            {targetPosition && !isMobileViewport && cardPosition.placement !== 'center' && (
              <span
                className="absolute h-4 w-4 rotate-45 border-slate-200 bg-white"
                style={
                  cardPosition.placement === 'top'
                    ? { bottom: -8, left: '50%', marginLeft: -8, borderRightWidth: 1, borderBottomWidth: 1 }
                    : cardPosition.placement === 'right'
                      ? { left: -8, top: '50%', marginTop: -8, borderLeftWidth: 1, borderBottomWidth: 1 }
                      : cardPosition.placement === 'left'
                        ? { right: -8, top: '50%', marginTop: -8, borderTopWidth: 1, borderRightWidth: 1 }
                        : { top: -8, left: '50%', marginLeft: -8, borderTopWidth: 1, borderLeftWidth: 1 }
                }
              />
            )}

            <div className="relative p-4 sm:p-4">
              <button
                type="button"
                onClick={() => finishTutorial('skipped')}
                className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                aria-label="Pular tutorial"
              >
                Pular
                <X className="h-3.5 w-3.5" />
              </button>

              <div>
                <div className="flex items-center gap-2 pr-16">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <CurrentIcon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                      Passo {currentStepIndex + 1} de {availableSteps.length}
                    </p>
                    <h2 className="text-xl font-semibold leading-tight text-slate-900">{currentStep.title}</h2>
                  </div>
                </div>

                <p className="mt-3 text-sm leading-6 text-slate-600">{currentStep.description}</p>
                {currentStep.bullets && (
                  <dl className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200">
                    {currentStep.bullets.map((bullet) => (
                      <div key={bullet.term} className="px-3 py-2">
                        <dt className="text-[13px] font-bold leading-tight text-emerald-700">
                          {bullet.term}
                        </dt>
                        <dd className="mt-0.5 text-[13px] leading-snug text-slate-600">
                          {renderDescriptionWithHighlights(bullet.description, bullet.highlights)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
                {showInteractionHint && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-emerald-800 shadow-[0_0_0_1px_rgba(16,185,129,0.15)]">
                    <MousePointerClick className="h-4 w-4 shrink-0 text-emerald-600" />
                    <span className="text-[11px] font-bold uppercase leading-snug">
                      {currentStep.interactionHint ?? 'Clique no destaque para continuar'}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-between gap-2">
                <Button
                  variant="outline"
                  onClick={goBackStep}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-primary"
                >
                  {currentStepIndex === 0 ? 'Voltar' : 'Anterior'}
                </Button>
                <div className="ml-auto flex items-center gap-2">
                  {showAdvanceButton && (
                    <Button onClick={() => advanceStep(currentStep.id)}>
                      {currentStepIndex >= availableSteps.length - 1 ? 'Concluir' : 'Seguir'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default FirstAccessTutorialModal;
